import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderDirections } from '@robocorp/robocloud-search-iternal-sdk';
import { Box, Button, Menu, useScreenSize } from '@robocorp/components';
import { IconSettingsSliders } from '@robocorp/icons/iconic';
import { FILTER_SETTINGS_SIZES } from '~/hooks/useFilterSettings';
import { FilterSettingsMenu, SettingsFilterMenuProps, useSettingsFilter } from './SettingsMenu';
import { FilterSortMenu, SettingsSortMenuProps, useSortFilter } from './SortMenu';

type Props = SettingsFilterMenuProps & SettingsSortMenuProps;

const CompactMenu: FC<Props> = ({
  columns,
  sort,
  visibility,
  size,
  sizes = FILTER_SETTINGS_SIZES,
  onSort,
  onVisibilityChange,
  onSizeChange,
}) => {
  const { t } = useTranslation();

  const { currentSortedColumn, sortableColumns, onSortDirectionChange, onSortOrderChange } =
    useSortFilter({
      columns,
      sort,
      onSort,
    });
  const { groups, onChange } = useSettingsFilter({ columns, visibility, onVisibilityChange });

  return (
    <Menu
      size="small"
      trigger={<Button variant="ghost" aria-label="Filter settings" icon={IconSettingsSliders} />}
      minWidth={240}
    >
      {sortableColumns.length > 0 && (
        <>
          <Menu.Title>{t('filter.sort.by')}</Menu.Title>
          {sortableColumns.map(({ id, title }) => (
            <Menu.Radio
              key={id}
              checked={id === sort?.[0]}
              onClick={() => onSortDirectionChange(id)}
            >
              {title}
            </Menu.Radio>
          ))}
          <Menu.Title>{t('filter.sort.order')}</Menu.Title>
          <Menu.Radio
            disabled={!currentSortedColumn}
            checked={sort?.[1] === OrderDirections.DESC}
            onClick={() => onSortOrderChange(sort?.[0], OrderDirections.DESC)}
          >
            {t('filter.sort.desc')}
          </Menu.Radio>
          <Menu.Radio
            disabled={!currentSortedColumn}
            checked={sort?.[1] === OrderDirections.ASC}
            onClick={() => onSortOrderChange(sort?.[0], OrderDirections.ASC)}
          >
            {t('filter.sort.asc')}
          </Menu.Radio>
        </>
      )}

      {groups.length > 0 && <Menu.Title>{t('filter.setting.visibility')}</Menu.Title>}
      {groups.map((group) => (
        <Fragment key={group ?? 'unnamed'}>
          {group && <Menu.Title>{group}</Menu.Title>}
          {columns.map((column) => {
            const columnTitle = column.title?.toString();
            return (
              columnTitle &&
              columnTitle.length > 0 &&
              column.groupName === group && (
                <Menu.Checkbox
                  key={column.id}
                  onClick={onChange(column.id)}
                  checked={!column.hidden && !visibility[column.id]}
                  disabled={column.required}
                >
                  {column.title}
                </Menu.Checkbox>
              )
            );
          })}
        </Fragment>
      ))}

      {sizes.length > 0 && <Menu.Title>{t('filter.setting.rows')}</Menu.Title>}
      {sizes.map((rowSize) => (
        <Menu.Radio key={rowSize} checked={rowSize === size} onClick={() => onSizeChange(rowSize)}>
          {rowSize}
        </Menu.Radio>
      ))}
    </Menu>
  );
};

export const FilterSettingsCompactMenu: FC<Props> = ({ columns, sort, onSort, ...props }) => {
  const isOnSmallerScreen = useScreenSize('l');

  return isOnSmallerScreen ? (
    <CompactMenu columns={columns} sort={sort} onSort={onSort} {...props} />
  ) : (
    <Box display="flex" justifyContent="flex-end" gap="$8" width={250}>
      <FilterSettingsMenu columns={columns} {...props} />
      <FilterSortMenu columns={columns} sort={sort} onSort={onSort} />
    </Box>
  );
};
